export const NACHA_PROCESSING_MESSAGES = {
  success: "NACHA processing completed successfully.",
  partial: "NACHA processing completed with some failures.",
  failure: "Unable to execute NACHA processing.",
};

const toSafeCount = value => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getResponsePayload = response => {
  if (
    response?.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
  ) {
    return response.data;
  }

  return response || {};
};

const getPayloadValue = (payload, key) => (
  payload?.[key] !== undefined ? payload[key] : payload?.data?.[key]
);

const getPayloadArray = (payload, key) => {
  if (Array.isArray(payload?.[key])) {
    return payload[key];
  }

  if (Array.isArray(payload?.data?.[key])) {
    return payload.data[key];
  }

  return [];
};

const findGeneratedRows = payload => getPayloadArray(payload, "generated");

const findFailedRows = payload => getPayloadArray(payload, "failed");

const findDetailRows = payload => {
  const generatedRows = findGeneratedRows(payload);
  const failedRows = findFailedRows(payload);

  if (generatedRows.length > 0 || failedRows.length > 0) {
    return [
      ...generatedRows,
      ...failedRows,
    ];
  }

  const detailKeys = [
    "details",
    "items",
    "generatedFiles",
    "files",
    "result",
    "results",
    "records",
  ];

  for (const key of detailKeys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }

    if (Array.isArray(payload?.data?.[key])) {
      return payload.data[key];
    }
  }

  return [];
};

const normalizeFailureValue = value => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object") {
    return (
      value.Message ||
      value.Description ||
      value.message ||
      value.description ||
      JSON.stringify(value)
    );
  }

  return String(value);
};

const getFirstValue = (row, keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) {
      return row[key];
    }
  }

  return "";
};

export const normalizeNachaDetailRows = rows => (
  Array.isArray(rows) ? rows : []
).map((row, index) => {
  const fileGenerationSuccess = getFirstValue(row, [
    "fileGenerationSuccess",
    "FileGenerationSuccess",
    "fileGenerated",
    "FileGenerated",
    "isFileGenerated",
    "IsFileGenerated",
    "status",
    "Status",
  ]);
  const reason = normalizeFailureValue(getFirstValue(row, [
    "reason",
    "Reason",
  ]));
  const error = normalizeFailureValue(getFirstValue(row, [
    "error",
    "Error",
  ]));

  return {
    id:
      getFirstValue(row, ["id", "_id", "transactionHeaderId", "TransactionHeaderId"]) ||
      index,
    merchantCode: getFirstValue(row, [
      "merchantCode",
      "MerchantCode",
      "nachaMerchantCode",
      "NACHAMerchantCode",
      "apiMerchantCode",
      "APIMerchantCode",
    ]),
    fileName: getFirstValue(row, [
      "fileName",
      "FileName",
      "nachaFileName",
      "NachaFileName",
      "generatedFileName",
    ]),
    accountTypeName: getFirstValue(row, [
      "accountTypeName",
      "AccountTypeName",
      "accountType",
      "AccountType",
    ]),
    startingTraceNumber: getFirstValue(row, [
      "startingTraceNumber",
      "StartingTraceNumber",
      "startTraceNumber",
      "StartTraceNumber",
    ]),
    numberOfRecords: getFirstValue(row, [
      "numberOfRecords",
      "NumberOfRecords",
      "recordCount",
      "RecordCount",
    ]),
    blockCount: getFirstValue(row, [
      "blockCount",
      "BlockCount",
    ]),
    sftpFilePath: getFirstValue(row, [
      "sftpFilePath",
      "SFTPFilePath",
      "sftpFileLocation",
      "SFTPFileLocation",
      "filePath",
      "FilePath",
    ]),
    sftpSaveTime: getFirstValue(row, [
      "sftpSaveTime",
      "SFTPSaveTime",
      "saveTime",
      "SaveTime",
      "createdDate",
      "CreatedDate",
      "createdAt",
    ]),
    fileGenerationSuccess,
    fileGenerated: fileGenerationSuccess,
    sftpSaveSuccess: getFirstValue(row, [
      "sftpSaveSuccess",
      "SFTPSaveSuccess",
      "sftpSaved",
      "SFTPSaved",
      "isSftpSaved",
      "IsSftpSaved",
    ]),
    reason: reason || "-",
    error: error || "-",
    failureReason: reason || error || "-",
    transactionHeaderId: getFirstValue(row, [
      "transactionHeaderId",
      "TransactionHeaderId",
      "headerId",
      "HeaderId",
    ]),
    raw: row,
  };
});

export const createNachaProcessingPayload = () => ({
  includeDetails: true,
});

export const createNachaProcessingFilterPayload = ({
  merchantCode,
  transactionHeaderId,
  startDate,
  endDate,
  status,
  searchText,
  pageNumber,
  pageSize,
} = {}) => {
  const payload = {};

  if (merchantCode) {
    payload.merchantCode = merchantCode;
  }

  if (transactionHeaderId) {
    payload.transactionHeaderId = transactionHeaderId;
  }

  if (status) {
    payload.SFTPStatus = String(status).toUpperCase();
  }

  if (startDate) {
    payload.StartDate = startDate;
  }

  if (endDate) {
    payload.EndDate = endDate;
  }

  if (searchText) {
    payload.searchText = searchText;
  }

  // include pagination when provided so every filter/search request
  // can be server-paginated (production-safe)
  if (pageNumber !== undefined && pageNumber !== null) {
    payload.PageNumber = pageNumber;
  }

  if (pageSize !== undefined && pageSize !== null) {
    payload.PageSize = pageSize;
  }

  return payload;
};

export const createFailureResult = () => ({
  message: NACHA_PROCESSING_MESSAGES.failure,
  variant: "error",
  status: "failure",
  summary: {
    generatedCount: 0,
    failedCount: 0,
    totalHeaders: 0,
    totalBatches: 0,
  },
  details: [],
});

export const normalizeNachaResponse = response => {
  const payload = getResponsePayload(response);
  const generatedRows = findGeneratedRows(payload);
  const failedRows = findFailedRows(payload);
  const backendMessage =
    payload?.Error?.Message ||
    payload?.Error?.Description ||
    payload?.message ||
    response?.message ||
    response?.data?.message ||
    "";
  const generatedCount = toSafeCount(
    getPayloadValue(payload, "generatedCount") ||
    getPayloadValue(payload, "generatedArrCount") ||
    (generatedRows.length > 0 ? generatedRows.length : undefined)
  );
  const failedCount = toSafeCount(
    getPayloadValue(payload, "failedCount") ||
    (failedRows.length > 0 ? failedRows.length : undefined)
  );
  const totalHeaders = toSafeCount(getPayloadValue(payload, "totalHeaders"));
  const totalBatches = toSafeCount(getPayloadValue(payload, "totalBatches"));
  const responseStatusCode =
    response?.statusCode ||
    response?.status ||
    payload.statusCode ||
    payload.httpStatus;
  const hasSummary = [
    "generatedCount",
    "failedCount",
    "totalHeaders",
    "totalBatches",
  ].some(key => (
    Object.prototype.hasOwnProperty.call(payload, key) ||
    Object.prototype.hasOwnProperty.call(payload?.data || {}, key)
  ));
  const details = normalizeNachaDetailRows(findDetailRows(payload));
  const pagination =
    payload?.pagination ||
    payload?.data?.pagination ||
    payload?.pagedResult ||
    null;
  const isPartial =
    Number(responseStatusCode) === 207 ||
    failedCount > 0 ||
    payload.partialSuccess === true;
  const isSuccessStatusFalse =
    payload?.IsSuccessStatusCode === false ||
    payload?.IsSuccessStatusCode === "false";
  const isFailure =
    isSuccessStatusFalse ||
    (payload.success === false &&
    !isPartial &&
    generatedCount === 0 &&
    failedCount === 0);

  if (isFailure) {
    return {
      ...createFailureResult(),
      message: backendMessage || NACHA_PROCESSING_MESSAGES.failure,
      summary: {
        generatedCount,
        failedCount,
        totalHeaders,
        totalBatches,
      },
      details,
      pagination,
    };
  }

  if (isPartial) {
    return {
      message:
        backendMessage ||
        NACHA_PROCESSING_MESSAGES.partial,
      variant: "warning",
      status: "partial",
      summary: {
        generatedCount,
        failedCount,
        totalHeaders,
        totalBatches,
      },
      details,
      pagination,
    };
  }

  if (payload.success === true || response?.status === true || hasSummary) {
    return {
      message:
        backendMessage ||
        NACHA_PROCESSING_MESSAGES.success,
      variant: "success",
      status: "success",
      summary: {
        generatedCount,
        failedCount,
        totalHeaders,
        totalBatches,
      },
      details,
      pagination,
    };
  }

  return createFailureResult();
};
